/**
 * 測定値と点検状態の解釈。
 *
 * ADM-25 / ADM-26 / BE-27。「値が無い」と「値が0」を同じ表示にしない。
 * 閾値の比較もここに集約し、一覧・詳細・集計・CSVで同じ判定にする。
 *
 * 超過は「閾値以上」（`data >= 閾値`、`ALCOHOL_EXCEED_THRESHOLD`）。サーバー
 * （`services/backend/api/alcohol_threshold.py`）・アプリ（`alcohol_judgement.dart`）と同じ。
 * 2026-10-11 の PM 判断で、0.15 mg/L ちょうどを超過に含める（法令上の基準値を安全側に扱うため。TH-AC3）。
 * 以前は「閾値を超える」（`>`）で、さらに前は画面が `>=`・サーバーが `>` で食い違っていた。
 */

/** サーバーの `ALCOHOL_EXCEED_THRESHOLD` と同じ値。変更時は両方を直す。 */
export const ALCOHOL_EXCEED_THRESHOLD = 0.15;

/** APIのフィルタ値。サーバーの判定（超過 = 閾値以上）に合わせる。 */
export const EXCEED_FILTER_PARAM = `data__gte=${ALCOHOL_EXCEED_THRESHOLD}`;

export type MeasurementState = 'exceeded' | 'detected' | 'clear' | 'unavailable';

/**
 * 測定値の状態。
 *
 * `null` / `undefined` / 数値でない値は `unavailable`。
 * 「未取得」を 0 として扱わない。
 */
export function measurementState(value: unknown): MeasurementState {
  const numeric = toNumber(value);
  if (numeric === null) return 'unavailable';
  if (numeric >= ALCOHOL_EXCEED_THRESHOLD) return 'exceeded';
  if (numeric > 0) return 'detected';
  return 'clear';
}

export function isExceeded(value: unknown): boolean {
  return measurementState(value) === 'exceeded';
}

/** 表示文字列。未取得は数値を出さない。 */
export function formatMeasurement(value: unknown, unavailableLabel = '未取得'): string {
  const numeric = toNumber(value);
  if (numeric === null) return unavailableLabel;
  return `${numeric.toFixed(2)} mg/L`;
}

/** 状態ごとの配色。文言と色の対応を1か所に持つ。 */
export const MEASUREMENT_STYLE: Record<MeasurementState, { text: string; badge: string }> = {
  exceeded: { text: 'text-red-600', badge: 'bg-red-200 py-1 px-2 rounded-md' },
  detected: { text: 'text-yellow-600', badge: 'bg-yellow-200 py-1 px-2 rounded-md' },
  clear: { text: 'text-green-600', badge: 'bg-green-200 py-1 px-2 rounded-md' },
  unavailable: { text: 'text-gray-500', badge: 'bg-gray-100 py-1 px-2 rounded-md' },
};

/** 機器の状態。内部値と表示の対応を1か所に持つ（ADM-25）。 */
export const DEVICE_STATUS = ['available', 'using', 'in_repair', 'damaged'] as const;
export type DeviceStatus = (typeof DEVICE_STATUS)[number];

export function isDeviceStatus(value: unknown): value is DeviceStatus {
  return typeof value === 'string' && (DEVICE_STATUS as readonly string[]).includes(value);
}

/** 点検結果。合格・不合格・未実施を区別する。 */
export type InspectionState = 'passed' | 'failed' | 'unavailable';

export function inspectionState(value: unknown): InspectionState {
  if (value === true) return 'passed';
  if (value === false) return 'failed';
  return 'unavailable';
}

export const INSPECTION_STYLE: Record<InspectionState, string> = {
  passed: 'text-green-600',
  failed: 'text-red-600',
  unavailable: 'text-gray-500',
};

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}
